import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, IconButton, CircularProgress, Alert, Chip, Checkbox,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import { Close, People, ExpandMore, ExpandLess, Search as SearchIcon } from '@mui/icons-material';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { TOUCH } from '../theme/tokens';
import AreaFilterRow from './common/AreaFilterRow';
import { CUSTOMER_SORT_OPTIONS, sortCustomers, addedLabel, SORT_NEWEST } from './common/CustomerSort';
import SortSelect from './common/SortSelect';
import ScrollChipRow from './common/ScrollChipRow';

const PAGE = 50;
const NONE = '__none__';        // "No area" / "No agent"
const NO_AREA = '__no_area__';  // cross filter: customers with no area
const NO_AGENT = '__no_agent__';

// One line of chips that scrolls sideways — agents are few, so no "+N more" sheet is needed.
function ChipRow({ allLabel, options, value, onChange }) {
    return (
        <ScrollChipRow sx={{ mt: 0.75 }}>
            {allLabel && (
                <Chip label={allLabel} size="small" clickable
                    color={!value ? 'primary' : 'default'} variant={!value ? 'filled' : 'outlined'}
                    onClick={() => onChange('')} />
            )}
            {options.map(o => (
                <Chip key={o.key} label={`${o.label} (${o.count})`} size="small" clickable
                    color={value === o.key ? (o.warn ? 'warning' : 'primary') : (o.warn ? 'warning' : 'default')}
                    variant={value === o.key ? 'filled' : 'outlined'}
                    onClick={() => onChange(value === o.key ? '' : o.key)} />
            ))}
        </ScrollChipRow>
    );
}

/**
 * Put customers into a group — an area or an agent — laid out like the product "Add
 * Customers" dialog so all three read the same way:
 *
 *   - "Already in <group> (n)" folded at the top, each with Remove
 *   - the list underneath shows only customers who could be added, filtered by
 *     "No <group>" (default) or "Other <groups>" (moves them here, after a confirm)
 *   - tick several, then one Add (n). Nothing saves on a tap, so rows never jump while
 *     the vendor is choosing.
 *
 * The parent owns the data and the saving; this owns only what is being chosen.
 *
 * Filters, both on one line each:
 *   - which group they are in now: "No agent" (the default — they need one), each other agent,
 *     or All. For areas it is the shared area row ("+N more" for the rest).
 *   - the other dimension: areas in the agent dialog (a round is picked by area), agents in the
 *     area dialog.
 *
 * customers      current (non-archived) customers
 * isMember(c)    already in this group
 * groupKeyOf(c)  id of the group the customer is in now, or null if none
 * groupNameOf(c) name of that group, for chips and rows ("Ravi", "Sudha (removed)")
 * labels         { group: 'area' | 'agent', none: 'No area', other: 'Other areas' }
 * areaOf(c)      optional: area name — shown on each row, and an area filter
 * agentOf(c)     optional: agent name — an agent filter (area dialog)
 * areaRankOf(c)  optional: the area's place in the route, for "Sort by Area"
 * onAdd(ids)     -> Promise<{ failed: number }>
 * onRemove(id)   -> Promise
 */
export default function AssignCustomersDialog({
    open, title, customers, isMember, groupKeyOf, groupNameOf, labels, areaOf, agentOf, areaRankOf, onAdd, onRemove, onClose,
}) {
    const keyOf = groupKeyOf || groupNameOf;
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState(NONE);         // NONE | a group key | '' = all
    const [areaFilter, setAreaFilter] = useState('');   // '' = all areas
    const [agentFilter, setAgentFilter] = useState(''); // '' = all agents
    const [sortBy, setSortBy] = useState(SORT_NEWEST);  // newest first: just-added customers lead
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(PAGE);
    const [showMembers, setShowMembers] = useState(false);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(null);
    const [confirmMove, setConfirmMove] = useState(false);
    const [message, setMessage] = useState(null);       // { severity, text }

    useEffect(() => {
        if (!open) return;
        setSearch(''); setFilter(NONE); setAreaFilter(''); setAgentFilter(''); setSelected([]);
        setPage(PAGE); setShowMembers(false); setSaving(false); setRemoving(null);
        setConfirmMove(false); setMessage(null);
    }, [open]);

    const q = search.trim().toLowerCase();
    const matches = (c) => !q || (c.f_name || '').toLowerCase().includes(q) || (c.f_mobile || '').includes(q);
    const byName = (a, b) => (a.f_name || '').localeCompare(b.f_name || '');

    // Plain computation: a few hundred customers, and the parent passes fresh functions on
    // every render, so memoising would recompute each time anyway.
    const members = customers.filter(isMember).sort(byName);
    const candidates = customers.filter(c => !isMember(c));
    // Which group they are in now. Counts come from everyone who could be added.
    const groupCounts = new Map();
    for (const c of candidates) {
        const k = keyOf(c);
        if (k == null) continue;
        const g = groupCounts.get(k) || { key: k, label: groupNameOf(c), count: 0 };
        g.count += 1;
        groupCounts.set(k, g);
    }
    const noneCount = candidates.filter(c => keyOf(c) == null).length;
    const groupOptions = [...groupCounts.values()]
        .map(g => ({ ...g, warn: /\(removed\)$/.test(g.label || '') }))
        .sort((a, b) => b.count - a.count || String(a.label).localeCompare(String(b.label)));
    const inFilter = candidates.filter(c => (!filter ? true : filter === NONE ? keyOf(c) == null : keyOf(c) === filter));

    // The other dimension, counted within the group chosen above, so each chip says what it shows.
    const countBy = (fn, noneKey) => {
        const m = new Map();
        for (const c of inFilter) {
            const v = fn(c) || noneKey;
            m.set(v, (m.get(v) || 0) + 1);
        }
        return m;
    };
    const areaCounts = areaOf ? countBy(areaOf, NO_AREA) : new Map();
    const agentCounts = agentOf ? countBy(agentOf, NO_AGENT) : new Map();

    const list = sortCustomers(inFilter
        .filter(c => !areaFilter || (areaOf(c) || NO_AREA) === areaFilter)
        .filter(c => !agentFilter || (agentOf(c) || NO_AGENT) === agentFilter)
        .filter(matches), sortBy, {
            areaRankOf,
            // the agent dialog knows agents as its own groups; the area dialog has agentOf
            agentOf: agentOf || (labels.group === 'agent' ? groupNameOf : undefined),
        });
    const visible = list.slice(0, page);

    const isSel = (id) => selected.includes(id);
    const toggle = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
    const allSel = list.length > 0 && list.every(c => isSel(c.f_id));
    const toggleAll = () => setSelected(s => (allSel
        ? s.filter(id => !list.some(c => c.f_id === id))
        : [...new Set([...s, ...list.map(c => c.f_id)])]));

    const movingCount = selected.filter(id => {
        const c = customers.find(x => x.f_id === id);
        return c && keyOf(c) != null;
    }).length;

    const doAdd = async () => {
        setConfirmMove(false);
        setSaving(true);
        setMessage(null);
        const ids = [...selected];
        try {
            const { failed = 0 } = (await onAdd(ids)) || {};
            const added = ids.length - failed;
            setSelected([]);
            setMessage(failed
                ? { severity: 'warning', text: `Added ${added}. ${failed} could not be added — please try again.` }
                : { severity: 'success', text: `Added ${added} customer${added !== 1 ? 's' : ''}.` });
        } finally {
            setSaving(false);
        }
    };
    const handleAdd = () => (movingCount ? setConfirmMove(true) : doAdd());

    const handleRemove = async (c) => {
        setRemoving(c.f_id);
        setMessage(null);
        try { await onRemove(c.f_id); } finally { setRemoving(null); }
    };

    const rowSx = {
        display: 'flex', alignItems: 'center', gap: 1, minHeight: TOUCH.min,
        px: 1, py: 0.25, borderBottom: '1px solid', borderColor: 'divider',
    };
    const meta = (c, extra) => [sortBy === SORT_NEWEST ? addedLabel(c) : null, c.f_mobile, areaOf && areaOf(c), extra]
        .filter(Boolean).join(' · ');

    return (
        <>
            <Dialog open={open} onClose={saving ? undefined : onClose} {...APP_DIALOG}
                // Fixed height: switching filters must not make the window jump in size.
                PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
                <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" noWrap sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
                    <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ color: 'inherit' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                {/* Search, filters and the folded "already in" row stay put while the list scrolls */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                    {/* Search ~2/3, Sort by the rest — as on the Customers page */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small" placeholder="Search by name or mobile"
                            value={search} onChange={e => { setSearch(e.target.value); setPage(PAGE); }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            sx={{ flex: 1, minWidth: 0 }}
                        />
                        <SortSelect value={sortBy} options={CUSTOMER_SORT_OPTIONS}
                            onChange={v => { setSortBy(v); setPage(PAGE); }} />
                    </Box>
                    {/* Where they are now. "No …" first and chosen by default: those still need one. */}
                    {labels.group === 'area' ? (
                        <AreaFilterRow
                            options={groupOptions}
                            noArea={{ key: NONE, count: noneCount }}
                            total={candidates.length}
                            value={filter}
                            onChange={key => { setFilter(key); setAreaFilter(''); setAgentFilter(''); setPage(PAGE); }}
                            sx={{ mt: 1 }}
                        />
                    ) : (
                        <ChipRow
                            allLabel={`All (${candidates.length})`}
                            options={[
                                ...(noneCount ? [{ key: NONE, label: labels.none, count: noneCount }] : []),
                                ...groupOptions,
                            ]}
                            value={filter}
                            onChange={key => { setFilter(key); setAreaFilter(''); setAgentFilter(''); setPage(PAGE); }}
                        />
                    )}
                    {/* The other dimension: areas for an agent, agents for an area */}
                    {areaOf && areaCounts.size > 1 && (
                        <AreaFilterRow
                            options={[...areaCounts.entries()].filter(([k]) => k !== NO_AREA)
                                .map(([k, n]) => ({ key: k, label: k, count: n }))}
                            noArea={{ key: NO_AREA, count: areaCounts.get(NO_AREA) || 0 }}
                            total={inFilter.length}
                            value={areaFilter}
                            onChange={key => { setAreaFilter(key); setPage(PAGE); }}
                            sx={{ mt: 0.75 }}
                        />
                    )}
                    {agentOf && agentCounts.size > 1 && (
                        <ChipRow
                            allLabel={`All agents (${inFilter.length})`}
                            options={[...agentCounts.entries()]
                                .map(([k, n]) => ({ key: k, label: k === NO_AGENT ? 'No agent' : k, count: n,
                                                    warn: /\(removed\)$/.test(k) }))
                                .sort((x, y) => (x.key === NO_AGENT) - (y.key === NO_AGENT) || y.count - x.count)}
                            value={agentFilter}
                            onChange={key => { setAgentFilter(key); setPage(PAGE); }}
                        />
                    )}
                    {members.length > 0 && (
                        <Box component="button" type="button" onClick={() => setShowMembers(v => !v)}
                            aria-expanded={showMembers}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1, width: '100%', mt: 1,
                                minHeight: TOUCH.min, px: 1.25,
                                border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5,
                                bgcolor: showMembers ? 'action.selected' : 'background.paper',
                                color: 'text.primary', font: 'inherit', textAlign: 'left', cursor: 'pointer',
                            }}>
                            <People fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                                Already in {labels.group === 'agent' ? 'this agent' : 'this area'} ({members.length})
                            </Typography>
                            <Typography variant="body2" color="primary.main" fontWeight={600}>{showMembers ? 'Hide' : 'Show'}</Typography>
                            {showMembers ? <ExpandLess fontSize="small" color="primary" /> : <ExpandMore fontSize="small" color="primary" />}
                        </Box>
                    )}
                </Box>

                <DialogContent sx={{ p: 0 }}>
                    {message && (
                        <Alert severity={message.severity} sx={{ m: 1.5 }} onClose={() => setMessage(null)}>{message.text}</Alert>
                    )}

                    {showMembers && (
                        <Box sx={{ bgcolor: 'action.hover', borderBottom: '2px solid', borderColor: 'divider' }}>
                            {members.filter(matches).map(c => (
                                <Box key={c.f_id} sx={{ ...rowSx, px: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{c.f_name}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{meta(c)}</Typography>
                                    </Box>
                                    <Button size="small" color="error" variant="outlined"
                                        disabled={saving || removing !== null}
                                        onClick={() => handleRemove(c)}
                                        sx={{ minHeight: TOUCH.dense, textTransform: 'none', flexShrink: 0 }}>
                                        {removing === c.f_id ? <CircularProgress size={16} color="inherit" /> : 'Remove'}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {list.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{list.length} to choose from</Typography>
                            <Button size="small" onClick={toggleAll} disabled={saving}
                                sx={{ textTransform: 'none', minWidth: 0 }}>
                                {allSel ? 'Unselect all' : 'Select all'}
                            </Button>
                        </Box>
                    )}

                    {list.length === 0 ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <People sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                {q ? 'No matches found'
                                    : filter === NONE && !areaFilter && !agentFilter
                                        ? `Every customer is already with ${labels.group === 'agent' ? 'an agent' : 'an area'}. Pick one above to move customers from it.`
                                        : 'No customers here'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {visible.map(c => {
                                const current = groupNameOf(c);
                                return (
                                    <Box key={c.f_id} onClick={() => !saving && toggle(c.f_id)}
                                        sx={{ ...rowSx, cursor: 'pointer', userSelect: 'none',
                                              bgcolor: isSel(c.f_id) ? 'action.selected' : 'background.paper' }}>
                                        <Checkbox checked={isSel(c.f_id)} disabled={saving} size="small"
                                            onClick={e => e.stopPropagation()} onChange={() => toggle(c.f_id)} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>{c.f_name}</Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{meta(c)}</Typography>
                                        </Box>
                                        {/* Where they are now — choosing them moves them from there */}
                                        {current && (
                                            <Chip label={current} size="small" variant="outlined" color="warning"
                                                sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0, maxWidth: 110 }} />
                                        )}
                                    </Box>
                                );
                            })}
                            {list.length > page && (
                                <Box sx={{ py: 2, textAlign: 'center' }}>
                                    <Button variant="outlined" size="small" onClick={() => setPage(p => p + PAGE)} sx={{ textTransform: 'none' }}>
                                        Load more ({list.length - page} remaining)
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 2, pb: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        {selected.length ? `${selected.length} selected` : ''}
                    </Typography>
                    <Button onClick={onClose} disabled={saving}>Done</Button>
                    <Button variant="contained" onClick={handleAdd} disabled={saving || !selected.length}
                        sx={{ minWidth: 110, height: TOUCH.min }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : `Add (${selected.length})`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Moving someone out of another area/agent changes that round too, so ask first */}
            <Dialog open={confirmMove} onClose={() => setConfirmMove(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Move {movingCount} customer{movingCount !== 1 ? 's' : ''}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {movingCount === 1 ? 'This customer is' : `These ${movingCount} customers are`} in
                        {labels.group === 'agent' ? ' another agent' : ' another area'} now.
                        They will be taken out of {movingCount === 1 ? 'it' : 'there'} and added here.
                        {labels.group !== 'agent' && ' They go to the end of this area\'s delivery order.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmMove(false)}>Cancel</Button>
                    <Button variant="contained" onClick={doAdd}>Move</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
